'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Input } from '@/core/components/ui/input';
import { cn } from '@/core/utils/cn';

export interface ComboboxOption {
    value: string | number;
    label: string;
    colorHex?: string | null;
    imagePath?: string | null;
}

interface ComboboxProps {
    options: ComboboxOption[];
    value?: string | number | null;
    onChange: (value: string | number) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    clearable?: boolean;
}

/**
 * Lightweight searchable single-select. The repo has no cmdk/popover library (only
 * @radix-ui/react-select, which isn't searchable) — this is a plain Input + an
 * absolutely-positioned filtered panel, deliberately not a new npm dependency. Modeled after
 * the client-side text-filter pattern already used elsewhere in the admin (e.g.
 * category-cross-sell-tab.tsx's filteredCategories).
 */
export function Combobox({ options, value, onChange, placeholder = 'Wybierz...', className, disabled, clearable }: ComboboxProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const selected = useMemo(() => options.find(o => String(o.value) === String(value)) ?? null, [options, value]);

    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        const q = query.trim().toLowerCase();
        return options.filter(o => o.label.toLowerCase().includes(q));
    }, [options, query]);

    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [open]);

    useEffect(() => {
        setHighlighted(0);
    }, [query, open]);

    const handleSelect = (opt: ComboboxOption) => {
        onChange(opt.value);
        setOpen(false);
        setQuery('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setOpen(false);
            setQuery('');
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) { setOpen(true); return; }
            setHighlighted(h => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const opt = filtered[highlighted];
            if (opt) handleSelect(opt);
        }
    };

    return (
        <div ref={rootRef} className={cn('relative', className)}>
            <div className="relative">
                <Input
                    ref={inputRef}
                    disabled={disabled}
                    value={open ? query : (selected?.label ?? '')}
                    placeholder={selected ? selected.label : placeholder}
                    onFocus={() => { setOpen(true); setQuery(''); }}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className={cn("pr-8 h-9 text-sm cursor-pointer", (selected?.colorHex || selected?.imagePath) && "pl-8")}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {clearable && selected && !disabled && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onChange(''); setQuery(''); }}
                            className="text-slate-300 hover:text-slate-600"
                            title="Wyczyść"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <ChevronDown className={cn('w-3.5 h-3.5 text-slate-400 transition-transform', open && 'rotate-180')} />
                </div>
                {selected?.colorHex ? (
                    <span
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border border-slate-300 pointer-events-none shrink-0"
                        style={{ backgroundColor: selected.colorHex }}
                    />
                ) : selected?.imagePath ? (
                    <img
                        src={selected.imagePath}
                        alt=""
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded object-cover pointer-events-none shrink-0"
                    />
                ) : null}
            </div>

            {open && (
                <div className="absolute z-30 top-full left-0 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl py-1">
                    {filtered.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-400 italic">Brak wyników</p>
                    ) : (
                        filtered.map((opt, i) => (
                            <button
                                key={opt.value}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
                                onMouseEnter={() => setHighlighted(i)}
                                className={cn(
                                    'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                                    i === highlighted ? 'bg-primary/10 text-primary' : 'text-slate-700 hover:bg-slate-50',
                                )}
                            >
                                {opt.colorHex && (
                                    <span className="w-4 h-4 rounded-full border border-slate-300 shrink-0" style={{ backgroundColor: opt.colorHex }} />
                                )}
                                {opt.imagePath && (
                                    <img src={opt.imagePath} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
                                )}
                                <span className="truncate flex-1">{opt.label}</span>
                                {String(opt.value) === String(value) && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
